import { configureStore } from "@reduxjs/toolkit";
import { rootActions } from "../reducers/root.slice";
import createSagaMiddleware from "redux-saga";
import rootSaga from "../sagas/root.saga";
import rootReducer from "./root-reducer";
import { workerActions } from "../reducers/worker.slice";

function createStore() {
  const sagaMiddleware = createSagaMiddleware({
    onError(error: Error) {
      console.error("Saga middleware crashed: " + error);
    },
  });

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: false,
        immutableCheck: false,
      }).prepend(sagaMiddleware),
    devTools: {
      actionSanitizer: (action) => {
        switch (action.type) {
          case rootActions.initialiseDone.type:
          case rootActions.convertToContinuousDone.type:
          case rootActions.applyReorderDone.type:
          case rootActions.revertToCategoricalDone.type:
            return {
              ...action,
              payload: {
                ...(action as any).payload,
                rows: "<<LONG_BLOB>>",
              },
            };

          case rootActions.setScatterPlotData.type:
          case rootActions.setHistogramData.type:
          case rootActions.setDensityPlotData.type:
            return {
              ...action,
              payload: "<<LONG_BLOB>>",
            };

          case rootActions.projectToPCADone.type:
            return {
              ...action,
              payload: {
                ...(action as any).payload,
                adjustedData: "<<LONG_BLOB>>",
              },
            };

          case workerActions.logisticRegression.type:
            return {
              ...action,
              payload: {
                ...(action as any).payload,
                trainingData: "<<LONG_BLOB>>",
              },
            };

          default:
            return action;
        }
      },
      stateSanitizer: (state) => {
        const typedState = state as unknown as TRootState;
        return (typedState
          ? {
              ...typedState,
              root: {
                ...typedState.root,
                rows: "<<LONG_BLOB>>",
                graphData: "<<LONG_BLOB>>",
                histogramData: "<<LONG_BLOB>>",
                densityPlotData: "<<LONG_BLOB>>",
              },
            }
          : typedState) as unknown as typeof state;
      },
    },
  });

  let sagaTask = sagaMiddleware.run(rootSaga);

  if (process.env.NODE_ENV !== "production" && module.hot) {
    module.hot.accept("./root-reducer", () => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const newRootReducer = require("./root-reducer");
      store.replaceReducer(newRootReducer);
    });

    module.hot.accept("../sagas/root.saga", () => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const newRootSaga = require("../sagas/root.saga");
      sagaTask.cancel();
      sagaTask.toPromise().then(() => {
        sagaTask = sagaMiddleware.run(newRootSaga.rootWatcher);
      });
    });
  }

  return store;
}

export type TRootState = ReturnType<typeof rootReducer>;

export default createStore;
